import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import { SelectCommunityChatType } from "../components/SelectCommunityChatType";

export const AddNewChannelModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    activeCommunityChatClientsList: subscribers = [],
    activeUserToRequest,
  } = locals;

  const { pushAccountInfo, pressAccountInfo, performSettings, pullAccountInfo } =
    activeUserToRequest;

  const pushEnabled = pushAccountInfo?.inAppCommunityEnabled;
  const pressEnabled = pressAccountInfo?.inAppCommunityEnabled;
  const performEnabled = performSettings?.inAppCommunityEnabled;
  const pullEnabled = pullAccountInfo?.inAppCommunityEnabled;

  const enabledTypes = useMemo(
    () =>
      [
        pushEnabled && "push",
        pressEnabled && "press",
        performEnabled && "perform",
        pullEnabled && "pull",
      ].filter(Boolean),
    [pushEnabled, pressEnabled, performEnabled, pullEnabled]
  );

  const hasMultipleTypes = enabledTypes.length > 1;

  const [channelNameState, setChannelNameState] = useState("");
  const [selectedChatType, setSelectedChatType] = useState([]);
  const [readOnly, setReadOnly] = useState(false);

  /**
   * Select the only active option if the coach only has 1 community chat enabled
   */
  useEffect(() => {
    if (enabledTypes.length === 1) {
      setSelectedChatType(enabledTypes);
    }
  }, [enabledTypes]);

  /**
   * Function to close the modal
   */
  const cancel = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        modal: "",
      },
    });
  };

  /**
   * To know if the button is enable or disable
   * @returns
   */

  const isButtonDisabled = () =>
    channelNameState.trim().length === 0 || selectedChatType.length === 0;

  /**
   * Function to call the action to create a channel
   * @param {*} e
   */
  const createChannel = (e) => {
    e.preventDefault();

    // Detect the types to be included
    const includeSubscribers = selectedChatType.includes("push");
    const includeMembers = selectedChatType.includes("press");
    const includePerform = selectedChatType.includes("perform");
    const includePull = selectedChatType.includes("pull");

    // Filter with the flags isSubscriber / isMember / isPerform / isPull
    const guests = [
      ...new Set(
        subscribers
          .filter(
            (u) =>
              (includeSubscribers && u.isSubscriber) ||
              (includeMembers && u.isMember) ||
              (includePerform && u.isPerform) ||
              (includePull && u.isPull)
          )
          .map(({ _id }) => _id)
      ),
    ];

    submit(`/actions/community-chat/create-channel`, {
      method: "POST",
      body: {
        subject: channelNameState,
        guests,
        types: selectedChatType,
        readOnly,
      },
    });
  };

  /**
   * Function to update the state of the input
   * @param {*} {target}
   */
  const handleChange = ({ target }) => {
    setChannelNameState(target.value);
  };

  const handleSetReadOnly = () => {
    setReadOnly(!readOnly);
  };

  return (
    <Modal
      className={"add-new-channel-modal"}
      title={t("communityChat.addNewChannelModal.title")}
    >
      <div className="body">
        <label htmlFor="channel-name">
          {t("communityChat.addNewChannelModal.channelNameLabel")}
        </label>
        <input
          id="channel-name"
          name="channelName"
          className="enter-channel-name"
          placeholder={t(
            "communityChat.addNewChannelModal.channelNamePlaceholder"
          )}
          value={channelNameState}
          onChange={handleChange}
        />
        {hasMultipleTypes && (
          <SelectCommunityChatType
            selectedType={selectedChatType}
            onSelect={setSelectedChatType}
            isEditing={false}
            user={activeUserToRequest}
          />
        )}
      </div>
      <footer className="flex justify-between! items-center">
        <div
          onClick={handleSetReadOnly}
          className="flex items-center gap-2 cursor-pointer"
        >
          <SvgIcon
            src={`/img/new-icons/${readOnly ? "" : "un"}checked-square.svg`}
            noFilterOnLight={readOnly ? true : false}
          />
          <p className="p4">
            {t("communityChat.addNewChannelModal.readOnlyChannel")}
          </p>
        </div>

        <div className="flex gap-2 items-center">
          <button data-button-type="secondary" onClick={cancel}>
            {t("communityChat.addNewChannelModal.cancel")}
          </button>

          <button
            data-button-type="primary"
            onClick={createChannel}
            disabled={isButtonDisabled()}
          >
            {t("communityChat.addNewChannelModal.next")}
          </button>
        </div>
      </footer>
    </Modal>
  );
};

AddNewChannelModal.propTypes = {
  locals: PropTypes.object,
};
